/**
 * useSponsoredSlots Hook
 * 
 * Fetches and manages sponsored/affiliate product slots.
 * Displays up to 6 affiliate tiles (2 rows × 3 cols).
 * 
 * Features:
 * - Priority 1: Manual Firestore entries (admin-created sponsored slots)
 * - Priority 2: AI-driven affiliate search (Amazon/Coupang via Google)
 * - Supports targeting by skin type, concerns, and region
 * - Tracks impressions when slots are displayed
 * - Provides loading and error states
 * 
 * @module hooks/useSponsoredSlots
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from '../services/firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  doc,
  setDoc,
  increment,
  Timestamp
} from 'firebase/firestore';
import { getAuthHeaders } from '../services/apiAuth';
import { getApiBaseUrl, nativeFetch } from '../utils/apiConfig';
import type { SponsoredSlot, GridProductCard } from '../types/product';
import type { MasterProfile } from '../types/profile';
import { calculateProfileCompletion } from '../services/profileService';
import {
  distributeSlots,
  buildAffiliateUrl,
  extractProductId,
  DEFAULT_NETWORKS,
  type NetworkConfig,
  type AffiliateNetwork,
} from '../utils/affiliateDistribution';
import { detectUserRegion, getRegionConfig, getSearchParams, getAmazonTagForRegion, type SupportedRegion } from '../config/affiliateConfig';
import { trackClickEvent } from '../services/eventTrackingService';
import { getCached, setCached, buildProfileCacheKey } from '../utils/affiliateSearchCache';
import { isRelevantBeautyProduct } from '../utils/productTitle';

// =============================================================================
// Brand extraction helpers (module-scoped for performance)
// =============================================================================

/** Network name → user-facing display label */
const NETWORK_DISPLAY: Record<AffiliateNetwork, string> = {
  amazon: 'Amazon', iherb: 'iHerb', yesstyle: 'YesStyle', coupang: 'Coupang',
};

/** Words that are never a brand on their own */
const GENERIC_TITLE_WORDS = /^(the|buy|best|top|new|set|for|and|with|from|pack|kit|mini)$/i;

/** Skincare ingredient abbreviations (optionally followed by strength digits), not brands */
const INGREDIENT_ABBREVS = /^(bha|aha|pha|spf|uva|uvb|oil)\d*$/i;

/**
 * Extract a plausible brand name from a Google Shopping title.
 * Skips leading generic/ingredient words and recovers the first meaningful
 * token (or "The X" two-word brands like "The Ordinary").
 * Returns empty string when nothing plausible is found.
 */
export function extractBrandFromTitle(title: string): string {
  // Capture alphanumeric tokens (SKIN1004, 3CE, CeraVe) — must contain at least one letter
  const tokens = (title.match(/[A-Za-z0-9][A-Za-z0-9'-]*/g) || [])
    .filter(t => t.length > 1 && /[A-Za-z]/.test(t));
  if (tokens.length === 0) return '';

  // Skip generic leading words and ingredient abbreviations
  let i = 0;
  while (i < tokens.length && (GENERIC_TITLE_WORDS.test(tokens[i]) || INGREDIENT_ABBREVS.test(tokens[i]))) {
    i++;
  }
  if (i >= tokens.length) return '';

  // Handle "The Ordinary" style two-word brands
  if (i > 0 && /^the$/i.test(tokens[i - 1])) {
    return `The ${tokens[i]}`;
  }

  return tokens[i];
}

// =============================================================================
// Types
// =============================================================================

export interface UseSponsoredSlotsOptions {
  /** Maximum number of slots to fetch (default: 6) */
  maxSlots?: number;
  /** User's skin type for targeting */
  skinType?: string;
  /** User's concerns for targeting */
  concerns?: string[];
  /** User's region for targeting */
  region?: SupportedRegion | 'ALL';
  /** Whether to auto-fetch on mount (default: true) */
  autoFetch?: boolean;
  /** Enable AI-driven affiliate fallback when no Firestore entries (default: true) */
  enableAffiliateFallback?: boolean;
  /** User's master profile for affiliate search queries */
  masterProfile?: MasterProfile;
}

export interface UseSponsoredSlotsReturn {
  /** Sponsored slots data */
  sponsoredSlots: SponsoredSlot[];
  /** Sponsored slots as GridProductCard for unified display */
  sponsoredProducts: GridProductCard[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Manually refresh sponsored slots */
  refetch: () => Promise<void>;
  /** Track a click on a sponsored slot */
  trackClick: (slotId: string) => Promise<void>;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useSponsoredSlots(
  options: UseSponsoredSlotsOptions = {}
): UseSponsoredSlotsReturn {
  const {
    maxSlots = 6,
    skinType,
    concerns,
    region = 'ALL',
    autoFetch = true,
    enableAffiliateFallback = true,
    masterProfile,
  } = options;

  const [sponsoredSlots, setSponsoredSlots] = useState<SponsoredSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generate search queries based on user profile
   * Priority: AI analysis keywords > user concerns > defaults
   */
  const generateSearchQueries = useCallback((profile: MasterProfile): string[] => {
    const queries: string[] = [];
    const userSkinType = profile.skin?.type || skinType || '';
    
    // Use AI analysis keywords if available (more accurate product matches)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aiKeywords = (profile.skin as any)?.aiAnalysis?.keywords as string[] | undefined;
    const userConcerns = aiKeywords?.slice(0, 6) || profile.skin?.concerns || concerns || [];
    
    // Profile-aware fields: brands, goals, age
    const preferredBrands = profile.skin?.preferredBrands || [];
    const skinGoals = profile.skin?.skinGoals || [];
    const ageGroup = profile.identity?.ageGroup || '';

    // English product keywords for Amazon (US market)
    const concernToProductsEN: Record<string, string[]> = {
      'acne': ['salicylic acid cleanser', 'BHA serum', 'tea tree spot treatment'],
      'pores': ['niacinamide serum', 'pore minimizing toner', 'clay mask'],
      'wrinkle': ['retinol serum', 'anti-aging cream', 'peptide moisturizer'],
      'aging': ['vitamin C serum', 'collagen cream', 'retinol night cream'],
      'hydration': ['hyaluronic acid serum', 'hydrating toner', 'moisturizing cream'],
      'dryness': ['ceramide moisturizer', 'facial oil', 'hydrating mask'],
      'brightening': ['vitamin C serum', 'niacinamide serum', 'brightening essence'],
      'dark spots': ['vitamin C serum', 'alpha arbutin serum', 'dark spot corrector'],
      'sensitive': ['gentle cleanser', 'calming serum', 'barrier repair cream'],
      'redness': ['centella asiatica cream', 'azelaic acid serum', 'soothing toner'],
      'oiliness': ['oil-free moisturizer', 'mattifying primer', 'salicylic acid cleanser'],
    };

    // Korean product keywords for Coupang (KR market)
    const concernToProductsKR: Record<string, string[]> = {
      'acne': ['살리실산 클렌저', 'BHA 세럼', '티트리 스팟 트리트먼트'],
      '여드름': ['살리실산 클렌저', 'BHA 세럼', '티트리 스팟'],
      'pores': ['나이아신아마이드 세럼', '모공 토너', '클레이 마스크'],
      '모공': ['나이아신아마이드 세럼', '모공 관리 토너', '클레이 마스크'],
      'wrinkle': ['레티놀 세럼', '주름 크림', '펩타이드 모이스처라이저'],
      '주름': ['레티놀 세럼', '안티에이징 크림', '펩타이드 크림'],
      'aging': ['비타민C 세럼', '콜라겐 크림', '레티놀 나이트 크림'],
      '노화': ['비타민C 세럼', '콜라겐 크림', '안티에이징 세럼'],
      'hydration': ['히알루론산 세럼', '수분 토너', '수분 크림'],
      '수분': ['히알루론산 세럼', '수분 토너', '수분 크림'],
      'dryness': ['세라마이드 크림', '페이셜 오일', '수분 마스크팩'],
      '건조': ['세라마이드 크림', '페이셜 오일', '수분 마스크팩'],
      'brightening': ['비타민C 세럼', '미백 에센스', '브라이트닝 토너'],
      '미백': ['비타민C 세럼', '미백 에센스', '브라이트닝 세럼'],
      'dark spots': ['비타민C 세럼', '알파알부틴 세럼', '다크스팟 코렉터'],
      '잡티': ['비타민C 세럼', '알파알부틴 세럼', '미백 크림'],
      'sensitive': ['저자극 클렌저', '진정 세럼', '장벽 크림'],
      '민감성': ['저자극 클렌저', '진정 세럼', '피부 장벽 크림'],
      'redness': ['시카 크림', '아젤라익산 세럼', '진정 토너'],
      '홍조': ['시카 크림', '병풀 크림', '진정 토너'],
      'oiliness': ['오일프리 크림', '매트 프라이머', '살리실산 클렌저'],
      '지성': ['오일 컨트롤 크림', '매트 프라이머', '모공 클렌저'],
    };

    // Goal-to-product mapping (supplements concern-based queries)
    const goalToProductsEN: Record<string, string> = {
      'brightening': 'vitamin C brightening serum',
      'anti-aging': 'retinol anti-aging serum',
      'hydration': 'hyaluronic acid hydrating serum',
      'acne control': 'salicylic acid acne treatment',
      'pore care': 'niacinamide pore serum',
      'firming': 'peptide firming cream',
      'soothing': 'centella soothing cream',
      'barrier repair': 'ceramide barrier cream',
    };

    const goalToProductsKR: Record<string, string> = {
      'brightening': '비타민C 브라이트닝 세럼',
      '미백': '비타민C 미백 세럼',
      'anti-aging': '레티놀 안티에이징 세럼',
      '안티에이징': '레티놀 안티에이징 세럼',
      'hydration': '히알루론산 수분 세럼',
      '수분': '히알루론산 수분 세럼',
      'acne control': '살리실산 여드름 트리트먼트',
      '여드름': '살리실산 여드름 케어',
      'pore care': '나이아신아마이드 모공 세럼',
      '모공': '나이아신아마이드 모공 세럼',
      'firming': '펩타이드 탄력 크림',
      '탄력': '펩타이드 탄력 크림',
      'soothing': '시카 진정 크림',
      '진정': '센텔라 진정 크림',
      'barrier repair': '세라마이드 장벽 크림',
      '장벽': '세라마이드 장벽 크림',
    };

    // Detect locale for product keywords
    const isKorean = navigator.language?.startsWith('ko') || region === 'KR';
    const concernToProducts = isKorean ? concernToProductsKR : concernToProductsEN;
    
    for (const concern of userConcerns) {
      const lowerConcern = concern.toLowerCase();
      for (const [key, products] of Object.entries(concernToProducts)) {
        if (lowerConcern.includes(key)) {
          const product = products[Math.floor(Math.random() * products.length)];
          queries.push(`${product} skincare`);
          break;
        }
      }
    }
    
    // Add skin goals as queries (deduplicated against existing concern queries)
    const goalProducts = isKorean ? goalToProductsKR : goalToProductsEN;
    for (const goal of skinGoals.slice(0, 2)) {
      const lowerGoal = goal.toLowerCase();
      const goalProduct = Object.entries(goalProducts)
        .find(([key]) => lowerGoal.includes(key))?.[1];
      if (goalProduct && !queries.some(q => q.includes(goalProduct.split(' ')[0]))) {
        queries.push(`${goalProduct} skincare`);
      }
    }

    if (userSkinType && queries.length > 0) {
      queries[0] = `${queries[0]} ${userSkinType.toLowerCase()} skin`;
    }

    // Add age-appropriate qualifier to first query
    // Canonical profile values include both '40s'/'50s' and '40-49'/'50-59' forms
    if (ageGroup && queries.length > 0) {
      const matureAges = ['40s', '50s', '60s', '70+', '40-49', '50-59', '60-69'];
      const teenAges = ['teens', '10s', 'under 18', '13-17'];
      if (matureAges.includes(ageGroup.toLowerCase())) {
        queries[0] = `${queries[0]} anti-aging`;
      } else if (teenAges.includes(ageGroup.toLowerCase())) {
        queries[0] = `${queries[0]} gentle`;
      }
    }

    // Preferred brands: add brand-specific queries (up to 2 slots)
    for (const brand of preferredBrands.slice(0, 2)) {
      if (queries.length < 6) {
        const brandQuery = userConcerns.length > 0
          ? `${brand} ${userConcerns[0]} skincare`
          : `${brand} skincare`;
        queries.push(brandQuery);
      }
    }
    
    if (queries.length === 0) {
      if (isKorean) {
        // Korean default queries for Coupang
        queries.push('스킨케어 세트 추천');
        queries.push('수분 세럼 보습');
        queries.push('저자극 클렌저 민감성 피부');
        queries.push('선크림 자외선 차단');
        queries.push('토너 패드 각질 제거');
        queries.push('나이트 크림 영양');
      } else {
        // English default queries for Amazon
        queries.push('best Korean skincare');
        queries.push('hydrating serum skincare');
        queries.push('gentle cleanser sensitive skin');
        queries.push('sunscreen SPF skincare');
        queries.push('retinol anti-aging serum');
        queries.push('niacinamide toner skincare');
      }
    }
    
    return queries.slice(0, 6);
  }, [skinType, concerns, region]);

  /**
   * Search for affiliate products via Google Search API
   * Priority: Amazon (US) > iHerb (Global) > YesStyle (K-Beauty)
   * For Korean users: Coupang (KR) > iHerb > YesStyle
   * 
   * Uses ratio-based distribution that adapts to any number of slots
   */
  const searchAffiliateProducts = useCallback(async (profile: MasterProfile): Promise<SponsoredSlot[]> => {
    const queries = generateSearchQueries(profile);
    // Auto-detect region from profile country (falls back to browser locale → US)
    const detectedRegion: SupportedRegion = region === 'ALL'
      ? detectUserRegion(profile?.identity?.country)
      : (region as SupportedRegion);
    const regionConfig = getRegionConfig(detectedRegion);
    const { gl, hl } = getSearchParams(detectedRegion, navigator.language?.startsWith('ko') ? 'ko' : 'en');
    
    // Affiliate configuration - from env vars
    const affiliateCodes = {
      amazon: getAmazonTagForRegion(detectedRegion) || import.meta.env.VITE_AMAZON_AFFILIATE_TAG || '',
      coupang: import.meta.env.VITE_COUPANG_PARTNER_ID || '',
      iherb: import.meta.env.VITE_IHERB_AFFILIATE_CODE || '',
      yesstyle: import.meta.env.VITE_YESSTYLE_AFFILIATE_ID || '',
    };
    
    // Build network configs based on region and configuration
    const primaryNetwork: NetworkConfig = regionConfig.primaryNetwork === 'coupang' && affiliateCodes.coupang
      ? { ...DEFAULT_NETWORKS.coupang, isConfigured: true }
      : { ...DEFAULT_NETWORKS.amazon, site: regionConfig.amazon?.domain ?? 'amazon.com', isConfigured: true };
    
    const secondaryNetworks: NetworkConfig[] = [
      { ...DEFAULT_NETWORKS.iherb, isConfigured: !!affiliateCodes.iherb },
      { ...DEFAULT_NETWORKS.yesstyle, isConfigured: !!affiliateCodes.yesstyle },
    ];

    // --- CACHE CHECK: return cached results if profile hasn't changed ---
    const profileSkinType = profile.skin?.type || '';
    const profileConcerns = profile.skin?.concerns || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aiKeywords = ((profile.skin as any)?.aiAnalysis?.keywords as string[] | undefined) || [];
    const aiFingerprint = aiKeywords.slice(0, 6).join(',');
    const brandsFingerprint = (profile.skin?.preferredBrands || []).slice(0, 3).join(',');
    const goalsFingerprint = (profile.skin?.skinGoals || []).slice(0, 3).join(',');
    const profileAgeGroup = profile.identity?.ageGroup || '';
    const cacheKey = buildProfileCacheKey('sponsored', profileSkinType, profileConcerns, detectedRegion, undefined, maxSlots, [aiFingerprint, brandsFingerprint, goalsFingerprint, profileAgeGroup].filter(Boolean).join(';'));
    const cached = getCached<SponsoredSlot[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Check auth before making API calls (prevents 401 when auto-refresh triggers before auth completes)
    const headers = await getAuthHeaders();
    if (!headers['X-Firebase-Auth']) {
      return [];
    }

    // Get ratio-based distribution for slots
    // This adapts to any number of slots (2, 3, 4, 6, 9, etc.)
    // Distribution example for 6 slots with 2:1 ratio: [amazon, iherb, amazon, yesstyle, amazon, iherb]
    const distribution = distributeSlots({
      primary: primaryNetwork,
      secondary: secondaryNetworks,
      totalSlots: maxSlots,
    });

    // --- BATCH: Group slots by network so we make ONE API call per network ---
    const networkGroups = new Map<string, { network: NetworkConfig; slotIndices: number[] }>();
    for (let i = 0; i < Math.min(maxSlots, distribution.length); i++) {
      const networkName = distribution[i];
      const network = networkName === primaryNetwork.name
        ? primaryNetwork
        : secondaryNetworks.find(n => n.name === networkName) || primaryNetwork;
      if (!networkGroups.has(networkName)) {
        networkGroups.set(networkName, { network, slotIndices: [] });
      }
      networkGroups.get(networkName)!.slotIndices.push(i);
    }

    // Helper: convert a Google Search result item into a SponsoredSlot
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toSlot = (item: any, network: NetworkConfig): SponsoredSlot => {
      let detectedNet: AffiliateNetwork = network.name;
      if (item.link.includes('coupang.com')) detectedNet = 'coupang';
      else if (item.link.includes('iherb.com')) detectedNet = 'iherb';
      else if (item.link.includes('yesstyle.com')) detectedNet = 'yesstyle';
      else if (item.link.includes('amazon.')) detectedNet = 'amazon';

      const productId = extractProductId(item.link, detectedNet);
      const affiliateUrl = buildAffiliateUrl(item.link, detectedNet, affiliateCodes);
      const networkConfig = detectedNet === primaryNetwork.name
        ? primaryNetwork
        : secondaryNetworks.find(n => n.name === detectedNet) || primaryNetwork;

      const image = item.pagemap?.cse_image?.[0]?.src
        || item.pagemap?.cse_thumbnail?.[0]?.src
        || '/images/placeholder-product.svg';
      const priceStr = item.pagemap?.offer?.[0]?.price
        || item.pagemap?.product?.[0]?.price
        || '';
      const priceUSD = parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
      const title = item.title || '';
      const parsedBrand = extractBrandFromTitle(title);
      const brand = parsedBrand || (NETWORK_DISPLAY[detectedNet] || 'Shop');

      return {
        id: `affiliate-${productId || crypto.randomUUID()}`,
        slotIndex: 0,
        product: {
          id: productId || `aff-${crypto.randomUUID()}`,
          name: title.slice(0, 60) + (title.length > 60 ? '...' : ''),
          brand,
          image,
          priceUSD,
          description: item.snippet?.slice(0, 100) || '',
        },
        affiliateUrl,
        affiliateSource: detectedNet,
        cpcBid: 0,
        revenueShare: networkConfig.commission,
        activeFrom: new Date().toISOString(),
        activeTo: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        isActive: true,
        impressions: 0,
        clicks: 0,
        revenue: 0,
      };
    };

    // Batch-fetch: diversify by using MULTIPLE queries per network when it has many slots.
    // This prevents a single concern (e.g. 'pores'→toner) from dominating all product tiles.
    const networkPools = new Map<string, SponsoredSlot[]>();
    let networkOffset = 0;
    await Promise.all(
      Array.from(networkGroups.entries()).map(async ([name, { network, slotIndices }]) => {
        const slotsNeeded = slotIndices.length;
        const allSlots: SponsoredSlot[] = [];
        // Use up to min(slotsNeeded, queries.length) distinct queries for diversity
        const queriesToUse = Math.min(slotsNeeded, queries.length);
        const baseOffset = (networkOffset++) % queries.length;
        for (let qi = 0; qi < queriesToUse; qi++) {
          const q = queries[(baseOffset + qi) % queries.length];
          const num = 3; // Fetch ~3 per query to spread across concerns
          try {
            const apiUrl = `${getApiBaseUrl()}/googleSearch`;
            const response = await nativeFetch(apiUrl, {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/json' },
              body: JSON.stringify({ query: q, num, siteRestrict: network.site, gl, hl }),
            });
            if (!response.ok) { continue; }
            const data = await response.json();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const slots = (data.items || [])
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .filter((item: any) => isRelevantBeautyProduct(item.title || '', item.link || '', item.snippet || ''))
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((item: any) => toSlot(item, network));
            allSlots.push(...slots);
          } catch (err) {
            console.warn(`Batch affiliate search failed for ${name} (query ${qi}):`, err);
          }
        }
        networkPools.set(name, allSlots.length > 0 ? allSlots : []);
      }),
    );

    // Sort pools: prefer product pages (/dp/) over search result pages (/s?k=)
    // Search pages show generic Amazon logo instead of product images (B2 fix)
    for (const [name, pool] of networkPools.entries()) {
      pool.sort((a, b) => {
        const aIsProduct = /\/dp\//.test(a.affiliateUrl) ? 0 : 1;
        const bIsProduct = /\/dp\//.test(b.affiliateUrl) ? 0 : 1;
        return aIsProduct - bIsProduct;
      });
      networkPools.set(name, pool);
    }

    // Fill slots from pools (dedup by URL AND by normalized product name to prevent
    // the same product appearing from different networks, e.g. Mediheal Heartleaf from
    // both the brand site and Amazon)
    const seenUrls = new Set<string>();
    const seenNormalizedNames = new Set<string>();
    const allResults: SponsoredSlot[] = [];

    /** Normalize title for dedup: lowercase, strip parentheticals,
     *  replace non-allowed chars with single space.
     *  Preserves Latin, CJK ideographs (Han/Hiragana/Katakana), and Korean characters. */
    const normalizeName = (title: string) =>
      title.toLowerCase().replace(/\([^)]*\)/g, '').replace(/[^a-z0-9\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff\uac00-\ud7a3]+/g, ' ').trim();

    for (let i = 0; i < Math.min(maxSlots, distribution.length); i++) {
      const networkName = distribution[i];
      const pool = networkPools.get(networkName) || [];
      const pick = pool.find(r => {
        const nn = normalizeName(r.product.name);
        return !seenUrls.has(r.affiliateUrl) && !seenNormalizedNames.has(nn);
      });
      if (pick) {
        seenUrls.add(pick.affiliateUrl);
        seenNormalizedNames.add(normalizeName(pick.product.name));
        pick.slotIndex = i;
        allResults.push(pick);
        pool.splice(pool.indexOf(pick), 1);
      } else if (networkName !== primaryNetwork.name) {
        const primaryPool = networkPools.get(primaryNetwork.name) || [];
        const fallback = primaryPool.find(r => {
          const nn = normalizeName(r.product.name);
          return !seenUrls.has(r.affiliateUrl) && !seenNormalizedNames.has(nn);
        });
        if (fallback) {
          seenUrls.add(fallback.affiliateUrl);
          seenNormalizedNames.add(normalizeName(fallback.product.name));
          fallback.slotIndex = i;
          allResults.push(fallback);
          primaryPool.splice(primaryPool.indexOf(fallback), 1);
        }
      }
    }

    // --- CACHE STORE ---
    if (allResults.length > 0) {
      setCached(cacheKey, allResults);
    }

    return allResults;
  }, [generateSearchQueries, region, maxSlots]);

  /**
   * Fetch active sponsored slots from Firestore
   */
  const fetchSponsoredSlots = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let slots: SponsoredSlot[] = [];
      
      // Try Firestore query first (may fail if index doesn't exist)
      try {
        const now = Timestamp.now();
        const sponsoredRef = collection(db, 'sponsoredSlots');
        
        // Query for active slots within date range
        const q = query(
          sponsoredRef,
          where('isActive', '==', true),
          where('activeFrom', '<=', now),
          where('activeTo', '>=', now),
          orderBy('activeFrom', 'desc'),
          orderBy('cpcBid', 'desc'), // Higher bids get priority
          limit(maxSlots * 2) // Fetch extra for filtering
        );

        const snapshot = await getDocs(q);
        
        slots = snapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data(),
        } as SponsoredSlot));
      } catch (firestoreErr) {
        // Firestore query failed (likely missing index) - continue with affiliate fallback
        console.warn('📦 Firestore query failed (index may be missing), falling back to affiliate search:', firestoreErr);
        slots = [];
      }

      // Apply targeting filters (client-side for flexibility)
      slots = slots.filter(slot => {
        const targeting = slot.targeting;
        if (!targeting) return true; // No targeting = show to everyone

        // Region targeting
        if (targeting.regions && targeting.regions.length > 0) {
          if (!targeting.regions.includes('ALL') && !targeting.regions.includes(region)) {
            return false;
          }
        }

        // Skin type targeting
        if (targeting.skinTypes && targeting.skinTypes.length > 0 && skinType) {
          if (!targeting.skinTypes.some(t => skinType.toLowerCase().includes(t.toLowerCase()))) {
            return false;
          }
        }

        // Concerns targeting
        if (targeting.concerns && targeting.concerns.length > 0 && concerns && concerns.length > 0) {
          const hasMatchingConcern = targeting.concerns.some(targetConcern =>
            concerns.some(userConcern => 
              userConcern.toLowerCase().includes(targetConcern.toLowerCase())
            )
          );
          if (!hasMatchingConcern) return false;
        }

        return true;
      });

      // Sort by slotIndex and take top maxSlots
      slots = slots
        .sort((a, b) => a.slotIndex - b.slotIndex)
        .slice(0, maxSlots);

      // If no Firestore slots and affiliate fallback enabled, search for affiliate products
      // Only use affiliate search for Phase 2 users (profile >= 50%) to avoid consuming search credits
      const isPhase2 = masterProfile ? calculateProfileCompletion(masterProfile) >= 50 : false;
      if (slots.length === 0 && enableAffiliateFallback && masterProfile && isPhase2) {
        // searchAffiliateProducts handles its own caching via shared affiliateSearchCache
        const affiliateSlots = await searchAffiliateProducts(masterProfile);
        if (affiliateSlots.length > 0) {
          slots = affiliateSlots;
        }
      }

      setSponsoredSlots(slots);

      // Track impressions for displayed slots (only for Firestore slots)
      await Promise.all(
        slots.filter(slot => !slot.id.startsWith('affiliate-')).map(slot => trackImpression(slot.id))
      );

    } catch (err) {
      console.error('Error fetching sponsored slots:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sponsored products');
      setSponsoredSlots([]);
    } finally {
      setIsLoading(false);
    }
  }, [maxSlots, skinType, concerns, region, enableAffiliateFallback, masterProfile, searchAffiliateProducts]);

  /**
   * Track impression for a sponsored slot
   */
  const trackImpression = async (slotId: string): Promise<void> => {
    try {
      const slotRef = doc(db, 'sponsoredSlots', slotId);
      await setDoc(slotRef, {
        impressions: increment(1),
        updatedAt: Timestamp.now(),
      }, { merge: true });
    } catch (err) {
      // Don't fail silently - log but don't throw
      console.warn('Failed to track impression for slot:', slotId, err);
    }
  };

  /**
   * Track click for a sponsored slot
   */
  const trackClick = useCallback(async (slotId: string): Promise<void> => {
    try {
      const slotRef = doc(db, 'sponsoredSlots', slotId);
      await setDoc(slotRef, {
        clicks: increment(1),
        updatedAt: Timestamp.now(),
      }, { merge: true });
    } catch (err) {
      console.warn('Failed to track click for slot:', slotId, err);
    }

    // Also record in clickEvents so admin dashboard affiliate tab shows data
    const slot = sponsoredSlots.find(s => s.id === slotId);
    trackClickEvent({
      productId: slot?.product?.id ?? slotId,
      slotId,
      placement: 'sponsored_row',
      slotIndex: slot ? sponsoredSlots.indexOf(slot) : 0,
      isPaidPlacement: true,
      affiliateSource: slot?.affiliateSource,
      affiliateUrl: slot?.affiliateUrl,
    }).catch(() => {});
  }, [sponsoredSlots]);

  /**
   * Convert SponsoredSlot to GridProductCard for unified display
   */
  const sponsoredProducts: GridProductCard[] = sponsoredSlots.map(slot => ({
    id: slot.id,
    name: slot.product.name,
    brand: slot.product.brand,
    image: slot.product.image,
    priceUSD: slot.product.priceUSD,
    url: slot.affiliateUrl,
    source: 'sponsored' as const,
    affiliateSource: slot.affiliateSource,
    isSponsored: true,
    trackingId: slot.id,
    reason: getAffiliateLabel(slot.affiliateSource),
  }));

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchSponsoredSlots();
    }
  }, [autoFetch, fetchSponsoredSlots]);

  return {
    sponsoredSlots,
    sponsoredProducts,
    isLoading,
    error,
    refetch: fetchSponsoredSlots,
    trackClick,
  };
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get display label for affiliate source
 */
function getAffiliateLabel(source: SponsoredSlot['affiliateSource']): string {
  switch (source) {
    case 'amazon':
      return 'Shop on Amazon';
    case 'coupang':
      return '쿠팡에서 구매';
    case 'partner':
      return 'Partner Product';
    case 'inhouse':
      return 'Heseda Official';
    case 'direct':
      return 'Direct Purchase';
    default:
      return 'Sponsored';
  }
}

export default useSponsoredSlots;
